import { useLatestRef, usePersistentCallback } from '@prophecy/utils/react/hooks';
import { useEffect, useRef, useState } from 'react';

import { buildTurns } from './chatTranscript';
import { UseQueuedHomeChatPromptsResult } from './useQueuedHomeChatPrompts';

import type { HomeChatMessages } from './chatTranscript';

export type UseQueuedHomeChatDispatcherInput = {
  conversationId: string | undefined;
  isWorking: boolean;
  /** A turn another tab started is running in this chat. */
  isRunningElsewhere?: boolean;
  historyLoading: boolean;
  historyMessages: HomeChatMessages;
  sandboxReady: boolean;
  /** Resolves false only when the broker refused the prompt before a turn started. */
  send: (conversationId: string, message: string, context?: Record<string, unknown>) => Promise<boolean>;
  queuedPrompts: UseQueuedHomeChatPromptsResult;
};

export type UseQueuedHomeChatDispatcherOutput = {
  /**
   * Call this before any user-driven submit (composer send or a widget follow-up). A reload with an
   * unanswered last turn pauses auto-draining the restored queue (see below) — this is what lifts
   * that pause once the user drives the conversation themselves, rather than it staying stuck.
   */
  notifyUserAction: () => void;
};

/**
 * Drains a per-conversation queue of prompts one at a time once the agent is free.
 *
 * Unlike product chat's dispatcher, `send` here resolves only once the whole turn completes (see
 * HomeChatSocket.chat / useHomeChatSession.send), so there is no separate "turn started" signal to
 * race against — `isWorking` already reflects "a turn is in flight" for the whole round trip. That
 * means no reducer/upload sub-states are needed: dispatch is just "pop, send, wait."
 */
export function useQueuedHomeChatDispatcher({
  conversationId,
  isWorking,
  isRunningElsewhere = false,
  historyLoading,
  historyMessages,
  sandboxReady,
  send,
  queuedPrompts
}: UseQueuedHomeChatDispatcherInput): UseQueuedHomeChatDispatcherOutput {
  // `isDispatching` (state, not a ref) is what lets the drain effect re-arm itself: once a send
  // finishes and this flips back to false, that's a real dependency change, so the effect re-fires
  // and picks up whatever head is current. A ref alone can't do this — flipping a ref produces no
  // render, so nothing would ever retry the next item once the first one's guard caught it.
  const [isDispatching, setIsDispatching] = useState(false);
  // Synchronous guard against a second concurrent dispatch within the same tick (e.g. StrictMode
  // double-invoking the drain effect) — `setIsDispatching` is scheduled, not immediate, so this
  // closes the window between the effect firing and a re-render reflecting the in-flight send.
  const inflightRef = useRef(false);
  const conversationIdRef = useLatestRef(conversationId);

  // `isWorking` covers this tab's own `send` and a turn the broker reports from another tab, but not
  // one that died with no tab to report it. If the loaded history's last turn has no closing reply
  // yet, auto-firing a restored queue into it risks a second turn with no user action behind it. So:
  // capture that once per conversation, as soon as its history has loaded, and block auto-dispatch
  // until the user acts or another tab's turn in the chat ends.
  const [suppressState, setSuppressState] = useState<{ conversationId: string | undefined; value: boolean }>({
    conversationId: undefined,
    value: false
  });
  useEffect(() => {
    if (historyLoading || suppressState.conversationId === conversationId) return;
    const turns = buildTurns(historyMessages);
    const lastTurnUnanswered = turns.length > 0 && turns[turns.length - 1].answer === undefined;
    setSuppressState({ conversationId, value: lastTurnUnanswered });
  }, [historyLoading, conversationId, historyMessages, suppressState.conversationId]);

  const lastConversationIdRef = useRef(conversationId);
  useEffect(() => {
    if (lastConversationIdRef.current === conversationId) return;
    lastConversationIdRef.current = conversationId;
    inflightRef.current = false;
    setIsDispatching(false);
  }, [conversationId]);

  // Tracked with the chat it belonged to: `isRunningElsewhere` is read off the open chat, so it also
  // falls true→false by switching away from a chat that is still running. Only the same chat ending
  // its turn is the signal to release the queue.
  const wasRunningElsewhereRef = useRef({ conversationId, value: isRunningElsewhere });
  useEffect(() => {
    const previous = wasRunningElsewhereRef.current;
    wasRunningElsewhereRef.current = { conversationId, value: isRunningElsewhere };
    const ended = previous.value && !isRunningElsewhere && previous.conversationId === conversationId;
    if (ended) setSuppressState({ conversationId, value: false });
  }, [isRunningElsewhere, conversationId]);

  const notifyUserAction = usePersistentCallback(() => {
    setSuppressState({ conversationId: conversationIdRef.current, value: false });
  });

  // Not-yet-evaluated (conversationId mismatch, e.g. the one render between historyLoading turning
  // false and the effect above settling) counts as suppressed — safe-by-default, not safe-until-proven-risky.
  const isSuppressed = suppressState.conversationId !== conversationId || suppressState.value;
  const headId = queuedPrompts.queue[0]?.id;
  const canDispatch =
    Boolean(conversationId) &&
    !isWorking &&
    !historyLoading &&
    !isSuppressed &&
    !isDispatching &&
    sandboxReady &&
    Boolean(headId);

  const dispatchHead = usePersistentCallback(async () => {
    const activeConversationId = conversationIdRef.current;
    if (!activeConversationId || inflightRef.current) return;
    const head = queuedPrompts.queue[0];
    if (!head) return;
    inflightRef.current = true;
    setIsDispatching(true);
    try {
      // Remove before send: send synchronously appends the prompt to the live thread, so leaving
      // the item queued would double-show it and risk a re-dispatch on the next drain.
      queuedPrompts.removeAt(head.id);
      const sent = await send(activeConversationId, head.text, head.context);
      // Back at the head, paused until the user acts, so a broker that keeps refusing cannot loop.
      if (!sent && conversationIdRef.current === activeConversationId) {
        queuedPrompts.restoreToFront(head);
        setSuppressState({ conversationId: activeConversationId, value: true });
      }
    } finally {
      // If the conversation changed mid-send, the switch effect above already reset both flags.
      if (conversationIdRef.current === activeConversationId) {
        inflightRef.current = false;
        setIsDispatching(false);
      }
    }
  });

  // Level-triggered drain: fires whenever conditions become true, including right after `isDispatching`
  // drops back to false once a send completes, which is what lets the next item take its turn.
  useEffect(() => {
    if (canDispatch) void dispatchHead();
  }, [canDispatch, dispatchHead]);

  return { notifyUserAction };
}
